import React from 'react';
import { useAiAccuracyReport } from '../hooks/useApi';
import type { AiAccuracyJobReport } from '../types';

// Format an ISO timestamp as Mountain Time: M/D/YYYY h:mm AM/PM MT
function formatMountainTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Denver',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) + ' MT';
  } catch {
    return isoString;
  }
}

function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400 dark:text-gray-500';
  if (score >= 3.5) return 'text-green-600 dark:text-green-400';
  if (score >= 2.5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function formatPct(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate)}%`;
}

function formatScore(score: number | null): string {
  if (score === null) return '—';
  return score.toFixed(2);
}

// Score distribution bucket order
const BUCKET_ORDER = ['0-1', '1-2', '2-3', '3-4', '4-5'];

const BUCKET_COLORS: Record<string, string> = {
  '0-1': 'bg-red-500',
  '1-2': 'bg-orange-400',
  '2-3': 'bg-amber-400',
  '3-4': 'bg-lime-500',
  '4-5': 'bg-green-500',
};

interface ScoreDistributionBarProps {
  distribution: Record<string, number>;
}

const ScoreDistributionBar: React.FC<ScoreDistributionBarProps> = ({ distribution }) => {
  const total = BUCKET_ORDER.reduce((sum, key) => sum + (distribution[key] ?? 0), 0);
  if (total === 0) {
    return (
      <p className="text-xs text-gray-400 dark:text-gray-500">No score distribution data.</p>
    );
  }

  return (
    <div>
      <div className="flex h-4 rounded overflow-hidden gap-0.5">
        {BUCKET_ORDER.map((key) => {
          const count = distribution[key] ?? 0;
          const pct = (count / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={key}
              className={`${BUCKET_COLORS[key]} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${key}: ${count} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-400 dark:text-gray-500">
        {BUCKET_ORDER.map((key) => {
          const count = distribution[key] ?? 0;
          const pct = (count / total) * 100;
          return (
            <div key={key} className="flex flex-col items-center">
              <span className={`inline-block w-2 h-2 rounded-sm ${BUCKET_COLORS[key]} mr-0.5`} />
              <span>{key}</span>
              <span className="font-medium text-gray-600 dark:text-gray-400">{count}</span>
              <span>{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface JobAccuracySectionProps {
  jobId: number;
}

const JobAccuracySection: React.FC<JobAccuracySectionProps> = ({ jobId }) => {
  const { data: report, isLoading, error } = useAiAccuracyReport();

  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center text-gray-500 dark:text-gray-400 text-sm">
        Loading accuracy data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500 dark:text-red-400 py-4">
        Failed to load accuracy report. The report may not be generated yet.
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-4">
        No accuracy report available.
      </div>
    );
  }

  const job: AiAccuracyJobReport | undefined = report.jobs.find((j) => j.jobId === jobId);

  if (!job) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-4">
        No accuracy data found for this job.{' '}
        <span className="text-xs">(Data fresh as of: {formatMountainTime(report.generatedAt)})</span>
      </div>
    );
  }

  const { accuracy, humanComparison } = job;

  return (
    <div className="space-y-6">
      {/* Freshness */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Data fresh as of: {formatMountainTime(report.generatedAt)}
      </p>

      {/* Coverage */}
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coverage</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Arbie reviewed{' '}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {job.aiReviewedCount}
          </span>{' '}
          of{' '}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {job.totalApplications}
          </span>{' '}
          applications ({job.aiReviewedPct.toFixed(0)}%)
        </p>
        {job.avgAiScore !== null && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Average AI score:{' '}
            <span className={`font-semibold ${scoreColor(job.avgAiScore)}`}>
              {formatScore(job.avgAiScore)}
            </span>
          </p>
        )}
      </div>

      {/* Score Distribution */}
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Score Distribution</p>
        <ScoreDistributionBar distribution={job.scoreDistribution} />
      </div>

      {/* Accuracy Metrics */}
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Accuracy Metrics</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Avg Score — Advanced</p>
            <p className={`text-lg font-bold ${scoreColor(accuracy.advancedAvgScore)}`}>
              {formatScore(accuracy.advancedAvgScore)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{accuracy.advancedCount} candidates</p>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Avg Score — Rejected</p>
            <p className={`text-lg font-bold ${scoreColor(accuracy.rejectedAvgScore)}`}>
              {formatScore(accuracy.rejectedAvgScore)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{accuracy.rejectedCount} candidates</p>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">High Score → Advanced</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatPct(accuracy.highScoreAdvanceRate)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">of high scorers advanced</p>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Low Score → Rejected</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatPct(accuracy.lowScoreRejectRate)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">of low scorers rejected</p>
          </div>
        </div>

        {accuracy.hiredCount > 0 && (
          <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 inline-flex gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Hired candidates</p>
              <p className="font-bold text-indigo-600 dark:text-indigo-400">{accuracy.hiredCount}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Avg AI score (hired)</p>
              <p className={`font-bold ${scoreColor(accuracy.hiredAvgScore)}`}>
                {formatScore(accuracy.hiredAvgScore)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Outcome Breakdown */}
      {job.outcomes.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Outcome by Stage</p>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Stage</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Count</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Avg AI Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {job.outcomes.map((outcome) => (
                  <tr
                    key={outcome.stage}
                    className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100 font-mono text-xs">
                      {outcome.stage}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                      {outcome.count}
                    </td>
                    <td className={`px-4 py-2 text-right font-medium ${scoreColor(outcome.avgAiScore)}`}>
                      {formatScore(outcome.avgAiScore)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Human Comparison */}
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Human Comparison</p>
        {humanComparison.humanReviewedCount > 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {humanComparison.humanOverrideCount}
            </span>{' '}
            of{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {humanComparison.humanReviewedCount}
            </span>{' '}
            human-reviewed applications had AI disagreement
            {humanComparison.humanReviewedCount > 0 && (
              <span className="text-gray-500 dark:text-gray-400 ml-1">
                ({((humanComparison.humanOverrideCount / humanComparison.humanReviewedCount) * 100).toFixed(0)}% override rate)
              </span>
            )}
          </p>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No human reviews recorded for this job yet.
          </p>
        )}
      </div>
    </div>
  );
};

JobAccuracySection.displayName = 'JobAccuracySection';

export default JobAccuracySection;
